import { setCurrentTransactionField } from "@/state/transactions/CurrentTransactionEvents";
import { useCurrentTransaction } from "@/state/transactions/CurrentTransactionHook";
import { CreateModelMail, ModelMail } from "@/type/core/models/models.types";
import { Form, FormInstance } from "antd";
import { useForm } from "antd/lib/form/Form";
import { useEffect, useState } from "react";

interface UseMessageSectionProps {
  invitation?: ModelMail;
  endOfTransaction?: ModelMail;
  isFromModel?: boolean;
}

interface UseMessageSectionReturn {
  form: FormInstance<any>;
  invitationSignatureInitialValue: string;
  setInvitationSignatureInitialValue: (newInitialValue: string) => void;
  invitationMailForm: FormInstance<CreateModelMail>;
  endOfTransactionSignatureInitialValue: string;
  setEndOfTransactionSignatureInitialValue: (newInitialValue: string) => void;
  endOfTransactionMailForm: FormInstance<CreateModelMail>;
  invitationMailFormValues: CreateModelMail;
  endOfTransactionMailFormValues: CreateModelMail;
  submittable: boolean;
  initialValues: { object: string };
}

const useMessageSection = ({
  invitation,
  endOfTransaction,
  isFromModel,
}: UseMessageSectionProps): UseMessageSectionReturn => {
  const [form] = useForm();
  const [invitationSignatureInitialValue, setInvitationSignatureInitialValue] =
    useState("");
  const [invitationMailForm] = useForm<CreateModelMail>();
  const [
    endOfTransactionSignatureInitialValue,
    setEndOfTransactionSignatureInitialValue,
  ] = useState("");
  const [endOfTransactionMailForm] = useForm<CreateModelMail>();
  const { transactionForm } = useCurrentTransaction();

  const values = Form.useWatch([], form);
  const invitationMailFormValues = Form.useWatch([], invitationMailForm);
  const endOfTransactionMailFormValues = Form.useWatch(
    [],
    endOfTransactionMailForm,
  );

  const initialValues = {
    object: transactionForm.reference ?? "",
  };
  const [submittable, setSubmittable] = useState(false);

  useEffect(() => {
    const invitationSignature =
      transactionForm.invitationMailConfiguration?.signature ??
      transactionForm.invitation?.mailConfiguration?.signature ??
      "";
    invitationMailForm.setFieldsValue({
      message:
        transactionForm.invitationMailConfiguration?.message ??
        transactionForm.invitation?.mailConfiguration?.message ??
        "",
      senderEmailLabel:
        transactionForm.invitationMailConfiguration?.sender?.type ??
        transactionForm.invitation?.mailConfiguration?.senderEmailLabel ??
        "",
      senderEmailLabelText:
        transactionForm.invitationMailConfiguration?.sender?.value ??
        transactionForm.invitation?.mailConfiguration?.senderEmailLabelText ??
        "",
      signature: invitationSignature,
      subject:
        transactionForm.invitationMailConfiguration?.subject ??
        transactionForm.invitation?.mailConfiguration?.subject ??
        "",
    });
    setInvitationSignatureInitialValue(invitationSignature);

    const endOfTransactionSignature =
      transactionForm.endOfTransactionMailConfiguration?.signature ??
      transactionForm.endOfTransaction?.mailConfiguration?.signature ??
      "";
    endOfTransactionMailForm.setFieldsValue({
      message:
        transactionForm.endOfTransactionMailConfiguration?.message ??
        transactionForm.endOfTransaction?.mailConfiguration?.message ??
        "",
      senderEmailLabel:
        transactionForm.endOfTransactionMailConfiguration?.sender?.type ??
        transactionForm.endOfTransaction?.mailConfiguration?.senderEmailLabel ??
        "",
      senderEmailLabelText:
        transactionForm.endOfTransactionMailConfiguration?.sender?.value ??
        transactionForm.endOfTransaction?.mailConfiguration
          ?.senderEmailLabelText ??
        "",
      signature: endOfTransactionSignature,
      subject:
        transactionForm.endOfTransactionMailConfiguration?.subject ??
        transactionForm.endOfTransaction?.mailConfiguration?.subject ??
        "",
    });
    setEndOfTransactionSignatureInitialValue(endOfTransactionSignature);
  }, [
    transactionForm.invitationMailConfiguration,
    transactionForm.invitation,
    transactionForm.endOfTransactionMailConfiguration,
    transactionForm.endOfTransaction,
  ]);

  useEffect(() => {
    if (form.getFieldValue("object") !== "") {
      setSubmittable(true);
      if (isFromModel) {
        setCurrentTransactionField([
          {
            field: "reference",
            value: form.getFieldValue("object"),
          },
        ]);
      }
    } else {
      setSubmittable(false);
    }
  }, [form, values, isFromModel]);

  useEffect(() => {
    const formModelMail: CreateModelMail = {
      message: invitation?.mailConfiguration?.message ?? "",
      senderEmailLabel: invitation?.mailConfiguration?.senderEmailLabel ?? "",
      senderEmailLabelText:
        invitation?.mailConfiguration?.senderEmailLabelText ?? "",
      signature: invitation?.mailConfiguration?.signature ?? "",
      subject: invitation?.mailConfiguration?.subject ?? "",
      defaultModel: invitation?.mailConfiguration?.isDefault ?? false,
    };
    invitationMailForm.setFieldsValue(formModelMail);
    setInvitationSignatureInitialValue(formModelMail.signature);
  }, [invitation, invitationMailForm]);

  useEffect(() => {
    const formModelMail: CreateModelMail = {
      message: endOfTransaction?.mailConfiguration?.message ?? "",
      senderEmailLabel:
        endOfTransaction?.mailConfiguration?.senderEmailLabel ?? "",
      senderEmailLabelText:
        endOfTransaction?.mailConfiguration?.senderEmailLabelText ?? "",
      signature: endOfTransaction?.mailConfiguration?.signature ?? "",
      subject: endOfTransaction?.mailConfiguration?.subject ?? "",
      defaultModel: endOfTransaction?.mailConfiguration?.isDefault ?? false,
    };
    endOfTransactionMailForm.setFieldsValue(formModelMail);
    setEndOfTransactionSignatureInitialValue(formModelMail.signature);
  }, [endOfTransaction, endOfTransactionMailForm]);

  return {
    form,
    invitationSignatureInitialValue,
    setInvitationSignatureInitialValue,
    invitationMailForm,
    endOfTransactionSignatureInitialValue,
    setEndOfTransactionSignatureInitialValue,
    endOfTransactionMailForm,
    invitationMailFormValues,
    endOfTransactionMailFormValues,
    submittable,
    initialValues,
  };
};

export default useMessageSection;
